import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, recursoService } from '../services/api';
import { App } from 'antd';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { message } = App.useApp();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [recursos, setRecursos] = useState([]);
  const [membresia, setMembresia] = useState(null);
  // El backend lo deriva de `recursos.es_lectura`, el mismo dato con el que
  // decide. Arranca en true para que un parpadeo de carga no muestre por un
  // instante botones que despues desaparecen.
  const [soloLectura, setSoloLectura] = useState(true);

  // Cargar recursos del usuario
  const cargarRecursos = async () => {
    try {
      const response = await recursoService.getMisRecursos();
      setRecursos(response.data.recursos || []);
      setMembresia(response.data.membresia);
      setSoloLectura(Boolean(response.data.solo_lectura));
    } catch (error) {
      console.error('Error al cargar recursos:', error);
      setRecursos([]);
      setMembresia(null);
      setSoloLectura(true);   // sin permisos confirmados, no se ofrece escribir
      // Sin este aviso el usuario no entiende nada: con `recursos` vacío,
      // tieneRecurso() da false para todo y ProtectedRoute lo empieza a rebotar
      // del sidebar sin decirle una palabra. Un console.error no lo ve nadie.
      message.error('No pudimos cargar tus permisos. Recargá la página.');
    }
  };

  // Verificar si hay sesión guardada al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      try {
        if (token && savedUser) {
          // Verificar que el token sigue siendo válido
          const response = await authService.getMe();
          setUser(response.data);
          await cargarRecursos();
          setIsAuthenticated(true);
        }
      } catch (error) {
        // Token vencido o inválido: se limpia y la app arranca deslogueada.
        // EXCEPCIÓN DELIBERADA a "nunca omitir message.error en un catch": esto
        // corre al abrir la app, y avisarle "tu sesión expiró" a alguien que
        // simplemente vuelve al día siguiente es ruido, no información. Ve la
        // pantalla de login, que es el mensaje.
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      } finally {
        // En finally: si localStorage tira (modo privado, cuota), sin esto
        // `loading` queda en true para siempre y la app no renderiza nunca.
        setLoading(false);
      }
    };

    checkAuth();
    // Corre UNA sola vez, al montar la app: restaura la sesión guardada.
    // `cargarRecursos` queda fuera de las deps a propósito; incluirla volvería
    // a disparar la restauración en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authService.login({ email, password });
      const { access_token, usuario } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(usuario));
      
      // Los recursos se cargan ANTES de flipear isAuthenticated. Al revés,
      // React flushea el batch mucho antes de que resuelva el fetch: PublicRoute
      // ve isAuthenticated=true, redirige por su cuenta, y recién después corre
      // el navigate() del caller. El destino final quedaba bien de casualidad,
      // pero se veía el flash de una pantalla intermedia que además montaba y
      // tiraba sus propios fetches. El estado que gatilla la redirección va último.
      setUser(usuario);
      await cargarRecursos();
      setIsAuthenticated(true);

      message.success(`¡Bienvenido, ${usuario.nom_usuario}!`);
      return { success: true, user: usuario };
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al iniciar sesión';
      message.error(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // El alta publica crea SIEMPRE un comprador: el rol y la cabaña los fuerza el
  // backend, nunca viajan desde acá. `eventoSlug` es el slug del micrositio de
  // donde vino la persona; si está, el backend le deja la solicitud de acceso a
  // esa cabaña ya pedida y en estado pendiente.
  const register = async ({ nombre, email, password, aceptaTerminos, eventoSlug }) => {
    try {
      const response = await authService.registro({
        nombre,
        email,
        password,
        acepta_terminos: aceptaTerminos,
        evento_slug: eventoSlug || undefined,
      });
      const { access_token, usuario, solicitud_cabana } = response.data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(usuario));

      // Mismo orden que en login: recursos primero, isAuthenticated al final.
      setUser(usuario);
      await cargarRecursos();
      setIsAuthenticated(true);

      message.success('¡Registro exitoso!');
      if (solicitud_cabana) {
        message.info('Pedimos tu acceso a la cabaña. Te avisamos cuando lo aprueben.');
      }
      // Se devuelve el usuario, igual que `login`. Sin esto el caller no
      // tenia con que resolver el destino y terminaba hardcodeando la ruta.
      return { success: true, user: usuario };
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Error al registrarse';
      message.error(errorMsg);
      // `code` viaja para que Register.jsx sepa qué hacer sin comparar textos.
      return { success: false, error: errorMsg, code: error.response?.data?.code };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setRecursos([]);
    setMembresia(null);
    // Vuelve al default fail-closed. Sin esto queda `recursos: []` con
    // `soloLectura: false` — una combinacion que no significa nada y que el
    // resto del codigo da por imposible. Hoy lo tapa que `login` espere a
    // `cargarRecursos` antes de autenticar; el dia que alguien reordene esas
    // lineas, la cabania de solo lectura ve por un frame los botones de crear.
    setSoloLectura(true);
    message.info('Sesión cerrada');
  };

  // Función para verificar si tiene un recurso
  const tieneRecurso = (codigo) => {
    return recursos.includes(codigo);
  };

  const updateUser = (nuevosDatos) => {
    setUser((prev) => ({ ...prev, ...nuevosDatos }));
    const saved = localStorage.getItem('user');
    if (saved) {
      localStorage.setItem('user', JSON.stringify({ ...JSON.parse(saved), ...nuevosDatos }));
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    recursos,
    membresia,
    soloLectura,
    login,
    register,
    logout,
    tieneRecurso,
    recargarRecursos: cargarRecursos,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
